import React, { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import api from "@/lib/api";

const DISMISS_KEY = "ts_pwa_dismissed";

const track = (event, props) => {
  try { window.posthog?.capture(event, props); } catch (_) {}
};
const logPwa = (event) => {
  try { api.post("/public/pwa-event", { event }).catch(() => {}); } catch (_) {}
};

// Lightweight "Install TaskScape" nudge: captures Android/desktop `beforeinstallprompt`,
// and shows manual Add-to-Home-Screen steps on iOS Safari (which has no such event).
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (standalone) return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    const ua = window.navigator.userAgent || "";
    const isIOS = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|crios|fxios|android).)*safari/i.test(ua);
    if (isIOS && isSafari) {
      setIos(true);
      setShow(true);
      track("pwa_install_prompt_shown", { platform: "ios" });
      logPwa("shown");
      return;
    }

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
      track("pwa_install_prompt_shown", { platform: "android_desktop" });
      logPwa("shown");
    };
    const onInstalled = () => {
      setShow(false);
      localStorage.setItem(DISMISS_KEY, "1");
      track("pwa_app_installed", {});
      logPwa("installed");
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
    track("pwa_install_dismissed", { platform: ios ? "ios" : "android_desktop" });
    logPwa("dismissed");
  };

  const install = async () => {
    if (!deferred) return;
    track("pwa_install_clicked", { platform: "android_desktop" });
    logPwa("clicked");
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      track("pwa_install_choice", { outcome: choice?.outcome || "unknown" });
    } catch (_) {}
    setDeferred(null);
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed z-[60] left-3 right-3 mx-auto max-w-md bottom-[calc(var(--ad-banner-h,64px)_+_12px)] osrs-panel p-3 flex items-center gap-3 chat-pop"
    >
      <img
        src="/icon-192.png"
        alt="TaskScape"
        width={40}
        height={40}
        className="shrink-0 rounded border-2 border-[#0f0d09]"
      />
      <div className="min-w-0 flex-1">
        <p className="osrs-title text-sm leading-tight">Install TaskScape</p>
        {ios ? (
          <p className="osrs-shadow text-[11px] text-[#b9a06a] leading-snug">
            Tap <Share size={11} className="inline text-[#FFCF3F] -mt-0.5" /> then{" "}
            <span className="text-[#E6A519]">
              &ldquo;Add to Home Screen&rdquo; <Plus size={10} className="inline -mt-0.5" />
            </span>{" "}
            for a fullscreen app.
          </p>
        ) : (
          <p className="osrs-shadow text-[11px] text-[#b9a06a] leading-snug">
            Add it to your home screen for a fullscreen, one-tap experience.
          </p>
        )}
      </div>
      {!ios && (
        <button
          data-testid="pwa-install-btn"
          onClick={install}
          className="osrs-btn osrs-btn-green px-3 py-2 text-xs flex items-center gap-1.5 shrink-0 min-h-[40px]"
        >
          <Download size={14} /> Install
        </button>
      )}
      <button
        data-testid="pwa-install-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="osrs-btn px-2 py-2 shrink-0 min-h-[40px] flex items-center justify-center"
      >
        <X size={14} />
      </button>
    </div>
  );
}
